export const PUBLIC_RUNTIME_CONFIG_KEYS = [
  "ACCOUNT_UI_URL",
  "API_BASE_URL",
  "API_TIMEOUT",
  "CMS_IMAGE_BASE_URL",
  "DD_RUM_ALLOWED_TRACING_URLS",
  "DD_RUM_APPLICATION_ID",
  "DD_RUM_CLIENT_TOKEN",
  "DD_RUM_ENV",
  "DD_RUM_EVENT_RATE_LIMIT",
  "DD_RUM_SAMPLE_RATE",
  "DD_RUM_SESSION_REPLAY_SAMPLE_RATE",
  "DD_RUM_TRACK_LONG_TASKS",
  "DD_RUM_TRACK_RESOURCES",
  "DD_RUM_TRACK_USER_INTERACTIONS",
  "DD_SERVICE",
  "DD_SITE",
  "DD_VERSION",
  "DEV_USER_EMAIL",
  "DISABLE_AUTH",
  "ENABLE_PERMISSION_DEBUG",
  "FILES_BASE_URL",
  "FRONTEGG_APP_ID",
  "FRONTEGG_BASE_URL",
  "FRONTEGG_CLIENT_ID",
] as const;

export type PublicRuntimeConfigKey = (typeof PUBLIC_RUNTIME_CONFIG_KEYS)[number];

export type PublicRuntimeConfigValues = Partial<Record<PublicRuntimeConfigKey, string>>;

export type PublicRuntimeConfigWindow = Window & {
  __L500_PUBLIC_CONFIG__?: Readonly<Record<string, unknown>>;
};

export type DatadogRumConfig = {
  applicationId: string | undefined;
  clientToken: string | undefined;
  site: string;
  service: string;
  env: string;
  version: string | undefined;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  allowedTracingUrls: string[];
  trackResources: boolean;
  trackLongTasks: boolean;
  trackUserInteractions: boolean;
  eventRateLimit: number | undefined;
};

const DEFAULT_API_TIMEOUT_MS = 10_000;

function read(key: PublicRuntimeConfigKey): string | undefined {
  const value = (window as PublicRuntimeConfigWindow).__L500_PUBLIC_CONFIG__?.[key];
  return typeof value === "string" && value !== "" ? value : undefined;
}

function isTrue(key: PublicRuntimeConfigKey): boolean {
  return read(key) === "true";
}

function isNotFalse(key: PublicRuntimeConfigKey): boolean {
  return (read(key) ?? "true").toLowerCase() !== "false";
}

function numberOr(key: PublicRuntimeConfigKey, fallback: number): number {
  const parsed = Number(read(key) ?? fallback);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function optionalNumber(key: PublicRuntimeConfigKey): number | undefined {
  const parsed = Number(read(key));
  return Number.isNaN(parsed) ? undefined : parsed;
}

function list(key: PublicRuntimeConfigKey): string[] {
  return (read(key) ?? "")
    .split(",")
    .map(value => value.trim())
    .filter(Boolean);
}

export const publicRuntimeConfig = {
  get authDisabled(): boolean {
    return isTrue("DISABLE_AUTH");
  },
  get frontegg(): { baseUrl: string; clientId: string | undefined; appId: string | undefined } {
    return {
      baseUrl: read("FRONTEGG_BASE_URL") ?? "",
      clientId: read("FRONTEGG_CLIENT_ID"),
      appId: read("FRONTEGG_APP_ID"),
    };
  },
  get apiBaseUrl(): string {
    return read("API_BASE_URL") ?? "/api";
  },
  get apiTimeoutMs(): number {
    const configured = Number(read("API_TIMEOUT"));
    return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_API_TIMEOUT_MS;
  },
  get filesBaseUrl(): string {
    return read("FILES_BASE_URL") ?? "https://www.legal500.com";
  },
  get cmsImageBaseUrl(): string | undefined {
    return read("CMS_IMAGE_BASE_URL");
  },
  get accountUiUrl(): string | undefined {
    return read("ACCOUNT_UI_URL");
  },
  get devUserEmail(): string {
    return read("DEV_USER_EMAIL") ?? "dev@legal500.com";
  },
  get permissionDebugEnabled(): boolean {
    return isTrue("ENABLE_PERMISSION_DEBUG");
  },
  get environmentName(): string | undefined {
    return read("DD_RUM_ENV");
  },
  get datadogRum(): DatadogRumConfig {
    return {
      applicationId: read("DD_RUM_APPLICATION_ID"),
      clientToken: read("DD_RUM_CLIENT_TOKEN"),
      site: read("DD_SITE") ?? "datadoghq.eu",
      service: read("DD_SERVICE") ?? "staff-ui",
      env: read("DD_RUM_ENV") ?? "markwyldelocal",
      version: read("DD_VERSION"),
      sessionSampleRate: numberOr("DD_RUM_SAMPLE_RATE", 100),
      sessionReplaySampleRate: numberOr("DD_RUM_SESSION_REPLAY_SAMPLE_RATE", 0),
      allowedTracingUrls: list("DD_RUM_ALLOWED_TRACING_URLS"),
      trackResources: isNotFalse("DD_RUM_TRACK_RESOURCES"),
      trackLongTasks: isNotFalse("DD_RUM_TRACK_LONG_TASKS"),
      trackUserInteractions: isNotFalse("DD_RUM_TRACK_USER_INTERACTIONS"),
      eventRateLimit: optionalNumber("DD_RUM_EVENT_RATE_LIMIT"),
    };
  },
};
